import clsx from "clsx";
import globalMU from "globalMU";
import * as R from "ramda";
import React, { useEffect, useState, useCallback, memo, useRef } from "react";
import InputMask from '../../libs/reactInputMask';
const estilosInput = require('../../estilos/inputs.module.scss').default;

export type InputMascaraProps = {
    valor?: string;
    onValorModificado: (valor: string) => void;
    onEnterPressionado?: () => void;
    emitirValorDeFormaSincrona?: boolean;
    mascara: string;
    emitirComMascara?: boolean;
    beforeMaskedValueChange?: any;
    disabled?: boolean;
    placeholder?: string;
    permitirMascaraIncompleta?: boolean;
    className?: string;
    id?: string;
    inputMode?: string
    type?: string
};

const InputMascaraComponent: React.FC<InputMascaraProps> = ({
    valor: propValor = "",
    onValorModificado,
    onEnterPressionado,
    emitirValorDeFormaSincrona,
    mascara,
    emitirComMascara,
    beforeMaskedValueChange,
    disabled,
    placeholder,
    inputMode,
    type,
    permitirMascaraIncompleta = false,
    className,
    id,
}) => {
    const [localValor, setLocalValor] = useState<string>(propValor ?? "");
    const [dirty, setDirty] = useState(false);
    const isMounted = useRef(true);

    // Cleanup para evitar vazamentos de memória
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Memoize the value treatment function
    const tratarValor = useCallback((valor: string) => {
        if (!isMounted.current) return null;
        
        return permitirMascaraIncompleta ? valor :
            globalMU.valorComMascaraEhValido(mascara, valor) && R.includes(emitirComMascara, [undefined, true]) ? valor :
            globalMU.valorComMascaraEhValido(mascara, valor) ? globalMU.removerMascara(mascara, valor) :
            null;
    }, [mascara, emitirComMascara, permitirMascaraIncompleta]);

    // Optimized change handler
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isMounted.current) return;
        
        const newValue = e.target.value;
        setLocalValor(newValue);
        setDirty(true);

        if (emitirValorDeFormaSincrona) {
            onValorModificado(tratarValor(newValue));
        }
    }, [emitirValorDeFormaSincrona, onValorModificado, tratarValor]);

    // Optimized blur handler
    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        if (!isMounted.current || !dirty) return;
        
        onValorModificado(tratarValor(e.target.value));
        setDirty(false);
    }, [dirty, onValorModificado, tratarValor]);

    // Optimized key down handler
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isMounted.current) return;
        
        if (e.key === "Enter") {
            onValorModificado(tratarValor(localValor));
            onEnterPressionado?.();
            setDirty(false);
        }
    }, [localValor, onValorModificado, onEnterPressionado, tratarValor]);

    // Only update local state when propValor actually changes
    useEffect(() => {
        if (isMounted.current) {
            setLocalValor(propValor ?? "");
        }
    }, [propValor]);

    return (
        <InputMask
            placeholder={placeholder}
            disabled={disabled}
            mask={mascara}
            id={id}
            inputMode={inputMode}
            type={type} 
            value={localValor ?? ""}
            beforeMaskedValueChange={beforeMaskedValueChange}
            onChange={handleChange}
            autoComplete="off"
            onBlur={handleBlur}
        >
            {() => (
                <input
                    className={clsx(className, estilosInput.inputPadrao)}
                    disabled={disabled}
                    onKeyDown={handleKeyDown}
                />
            )}
        </InputMask>
    );
};

export const InputMascara = memo(InputMascaraComponent);